import { plannerPlanThunks } from '@ucheba/store/planner/plan'
import { useDispatch, useSelector } from 'react-redux'
import { authSelectors } from '@ucheba/store/auth'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDialog } from '@ucheba/ui/components/Dialog/bll'
import { plannerProgramsThunks } from '@ucheba/store/planner/programs'
import { plannerSpecialtiesThunks } from '@ucheba/store/planner/specialties'
import { removeQueryParameters, stringifyUrl } from '@ucheba/utils/helpers/router'
import { coreQuery } from '@ucheba/utils/constants/queries'
import { getParsedCookies } from '@ucheba/utils/helpers/core'
import { authCookieToken } from '@ucheba/utils/constants/core'
import { dispatchClosedAuthMiddleware } from '../../../../../../utils/helpers/dispatchClosedAuthMiddleware'

interface IProps {
  id: number
  ids: number[]
  type?: 'institution' | 'program' | 'specialities'
  onSelect?: (id: number) => void
  offSelect?: (id: number) => void
}

interface IUseWantToStudy {
  (props: IProps): {
    isActive: boolean
    handleWantToStudy: () => Promise<void>
    onClose: () => void
    isInPlanOfAdmission: boolean
    handleToggleSpecialityAdded: () => void
    handleToggleCardAdded: () => void
    handleToggleProgramAdded: () => void
    openSpecialityAdded: boolean
    openCardAdded: boolean
    openProgramAdded: boolean
  }
}

export const useWantToStudy: IUseWantToStudy = (props) => {
  const { id, ids, type = 'institution', onSelect, offSelect } = props
  const isAuth = useSelector(authSelectors.isValidToken)
  const { push, query, replace, asPath } = useRouter()
  const dialog = useDialog('educationalInstitutionAddedNotificationId')
  const dispatch = useDispatch()

  // вуз или программа уже в плане?
  const isInPlanOfAdmission = useMemo(() => {
    if (!isAuth) return false

    if (ids && id) {
      return ids.includes(id)
    }

    return false
  }, [isAuth, ids, id])

  const [openSpecialityAdded, setOpenSpecialityAdded] = useState(false)
  const [openCardAdded, setOpenCardAdded] = useState(false)
  const [openProgramAdded, setOpenProgramAdded] = useState(false)

  const handleToggleSpecialityAdded = useCallback(
    () => setOpenSpecialityAdded((prevValue) => !prevValue),
    []
  )
  const handleToggleCardAdded = useCallback(
    () => setOpenCardAdded((prevValue) => !prevValue),
    []
  )
  const handleToggleProgramAdded = useCallback(
    () => setOpenProgramAdded((prevValue) => !prevValue),
    []
  )

  const params = useMemo(() => {
    const result = {}

    Object.keys(query).forEach((key) => {
      const newKey = key.replace('[]', '')

      if (key.includes('[]') && !Array.isArray(query[key])) {
        result[newKey] = [query[key]]
      } else {
        result[newKey] = query[key]
      }
    })

    return result
  }, [query])

  const authRedirect = useCallback(() => {
    const url = removeQueryParameters(asPath)

    push(
      stringifyUrl('/auth', {
        set: {
          [coreQuery.redirect]: stringifyUrl(url, {
            set: { ...params, wantToStudy: id },
          }),
        },
      })
    )
  }, [asPath, id, params, push])

  // обработчик клика на кнопку
  const handleWantToStudy = useCallback(async () => {
    const parsedCookie = getParsedCookies()
    const tokenString = parsedCookie[authCookieToken]

    if (!tokenString || !isAuth) {
      authRedirect()

      return
    }

    if (isInPlanOfAdmission) {
      if (type === 'program') {
        await dispatchClosedAuthMiddleware(async () => {
          return dispatch(
            plannerProgramsThunks.deletePrograms({
              data: {
                entityIds: [id],
              },
            })
          )
        }, authRedirect)
      }
      if (type === 'institution') {
        await dispatchClosedAuthMiddleware(async () => {
          return dispatch(
            plannerPlanThunks.deleteUz({
              data: {
                entityIds: [id],
              },
            })
          )
        }, authRedirect)
      }
      if (type === 'specialities') {
        await dispatchClosedAuthMiddleware(async () => {
          return dispatch(
            plannerSpecialtiesThunks.deleteSpecialities({
              data: {
                entityIds: [id],
              },
            })
          )
        }, authRedirect)
      }

      if (offSelect) {
        offSelect(id)
      }
    } else {
      if (type === 'program') {
        await dispatchClosedAuthMiddleware(async () => {
          return dispatch(
            plannerProgramsThunks.postPrograms({
              data: {
                entityIds: [id],
              },
            })
          )
        }, authRedirect)

        if (!localStorage.getItem('programInstitutionAddedNotification')) {
          handleToggleProgramAdded()
        }
      }
      if (type === 'institution') {
        await dispatchClosedAuthMiddleware(async () => {
          return dispatch(
            plannerPlanThunks.postUz({
              data: {
                entityIds: [id],
                institutionId: id,
              },
            })
          )
        }, authRedirect)

        if (!localStorage.getItem('educationalInstitutionAddedNotification')) {
          handleToggleCardAdded()
        }
      }
      if (type === 'specialities') {
        await dispatchClosedAuthMiddleware(async () => {
          return dispatch(
            plannerSpecialtiesThunks.postSpecialities({
              data: {
                entityIds: [id],
              },
            })
          )
        }, authRedirect)

        if (!localStorage.getItem('specialityInstitutionAddedNotification')) {
          handleToggleSpecialityAdded()
        }
      }

      if (!localStorage.getItem('specialityInstitutionAddedNotification')) {
        dialog.openDialog()
      }

      if (onSelect) {
        onSelect(id)
      }
    }
    if (type === 'program') {
      await dispatch(plannerProgramsThunks.getProgramIds({}))
    }
    if (type === 'institution') {
      await dispatch(plannerPlanThunks.getUzIds({}))
    }
    if (type === 'specialities') {
      await dispatch(plannerSpecialtiesThunks.getSpecialitiesIds({}))
    }
  }, [
    asPath,
    params,
    isAuth,
    isInPlanOfAdmission,
    type,
    push,
    offSelect,
    dispatch,
    id,
    onSelect,
    handleToggleProgramAdded,
    handleToggleCardAdded,
    handleToggleSpecialityAdded,
    dialog,
  ])

  // если в урле есть wantToStudy: true то добавляем ид в план и удаляем wantToStudy из урла
  useEffect(() => {
    if (query.wantToStudy && isAuth && String(id) === query.wantToStudy) {
      delete query.wantToStudy

      replace(
        stringifyUrl(removeQueryParameters(asPath), {
          set: {
            ...params,
            wantToStudyDone: 1, // костыль чтоб избежать перехода на SSG страницу
          },
          exclude: ['wantToStudy'],
        }),
        undefined,
        { shallow: true }
      )

      handleWantToStudy()
    }
  }, [])

  return {
    handleWantToStudy,
    isActive: dialog.isActive,
    onClose: dialog.closeDialog,
    isInPlanOfAdmission,
    openSpecialityAdded,
    openCardAdded,
    openProgramAdded,
    handleToggleCardAdded,
    handleToggleProgramAdded,
    handleToggleSpecialityAdded,
  }
}
